import React, { ChangeEvent, FC, ReactElement, useCallback, useEffect } from 'react';
import { Button, Col, FormGroup, Input, Label, Row } from 'reactstrap';
import { FormNodeSimpleLinkedTermsModel } from '../../../../../shared/dealer';
import { SettingTermKeys, ValidSettingTermKeys } from '../../../../../shared/settings';
import { MaterialIcon } from '../../../../components';
import { useTranslation } from '../../../../translations';
import { useInputState } from '../../../../utils';
import { AbstractElementEditor, UpdateFormProps } from './AbstractElementEditor';

export class SimpleLinkedTermsEditor extends AbstractElementEditor<FormNodeSimpleLinkedTermsModel> {
  public getDescription(): string {
    return 'Customizable Terms and Conditions component.';
  }

  public getName(): string {
    return 'Terms and Conditions';
  }

  public getIcon(): string {
    return 'text_snippet';
  }

  public override getDefaultValues(): Partial<Omit<FormNodeSimpleLinkedTermsModel, 'id' | 'type'>> {
    return {
      displayName: 'Vendor Terms',
      termsKey: 'vendor',
      agreementType: 'checkbox',
      requireChecked: true,
      showPreview: false,
    };
  }

  public override getUpdateForm({
    node,
    update,
  }: UpdateFormProps<FormNodeSimpleLinkedTermsModel>): ReactElement {
    return <SimpleLinkedTermsUpdateForm node={node} update={update} />;
  }
}

const SimpleLinkedTermsUpdateForm: FC<
  Omit<UpdateFormProps<FormNodeSimpleLinkedTermsModel>, 'ts'>
> = ({ node, update }) => {
  const { ts } = useTranslation();
  const [agreementLine, setAgreementLine, setAgreementLineRaw] = useInputState(node.displayName);

  const resetText = useCallback(() => {
    const linkTermsKeys = new Set(['terms', 'privacy', 'vendor']);
    const link = linkTermsKeys.has(node.termsKey)
      ? `[/policy/${node.termsKey}](Terms and Conditions)`
      : 'Terms and Conditions';

    const text = ts(
      node.agreementType === 'name'
        ? 'dealer_form.i_understand_and_agree_to'
        : 'dealer_form.name_agreement',
    );

    setAgreementLineRaw(`${text} ${link}`);
  }, [node.termsKey, node.agreementType, ts, setAgreementLineRaw]);

  const updateAgreementType = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const agreementType = e.target.value as 'checkbox' | 'name';
      update({ ...node, agreementType });
      resetText();
    },
    [update, node, resetText],
  );

  useEffect(() => {
    update({ ...node, displayName: agreementLine });
  }, [agreementLine]);

  return (
    <>
      <Label for="dealerSimpleTermsKey">{ts('express.term_key')}</Label>
      <Input
        defaultValue={node.termsKey}
        id="dealerSimpleTermsKey"
        onChange={(e) => {
          update({ ...node, termsKey: e.target.value as SettingTermKeys });
        }}
        type="select"
      >
        <option value="none">{ts('none')}</option>
        {ValidSettingTermKeys.map((v) => {
          return (
            <option key={v} value={v}>
              {v}
            </option>
          );
        })}
      </Input>
      <Label for="dealerSimpleTermsDisplay">
        {ts('option_editor_select.display_name_optional')}
      </Label>
      <Input
        defaultValue={node.displayName}
        id="dealerSimpleTermsDisplay"
        onChange={(e) => {
          update({ ...node, displayName: e.target.value });
        }}
        type="text"
      />
      <Label for="dealerSimpleTermsAgreement">{ts('express.agreement_method')}</Label>
      <Input id="dealerSimpleTermsAgreement" onChange={updateAgreementType} type="select">
        <option value="checkbox">{ts('express.checkbox')}</option>
        <option value="name">{ts('express.full_name')}</option>
      </Input>
      <Row>
        <Col sm="12">
          <Label for="dealerSimpleTermsDisplay">{ts('agreement_line')}</Label>
        </Col>
        <Col sm="12">
          <Button onClick={resetText} style={{ float: 'right' }} title={ts('common.reset')}>
            <MaterialIcon name="restart_alt" />
          </Button>
          <Input
            defaultValue={node.displayName}
            id="dealerSimpleTermsDisplay"
            onChange={setAgreementLine}
            style={{ width: 'calc(100% - 60px)' }}
            type="text"
            value={agreementLine}
          />
        </Col>
      </Row>

      <br />
      <FormGroup check>
        <Input
          defaultChecked={node.requireChecked}
          id="dealerSimpleTermsRequired"
          onChange={(e) => {
            update({ ...node, requireChecked: e.target.checked });
          }}
          type="checkbox"
        />
        <Label check for="dealerSimpleTermsRequired">
          {ts('express.require_agreement')}
        </Label>
      </FormGroup>
      <br />
      <FormGroup check>
        <Input
          defaultChecked={node.showPreview}
          id="dealerSimpleTermsShowPreview"
          onChange={(e) => {
            update({ ...node, showPreview: e.target.checked });
          }}
          type="checkbox"
        />
        <Label check for="dealerSimpleTermsShowPreview">
          {ts('express.show_preview_terms')}
        </Label>
      </FormGroup>
    </>
  );
};
